import React, { useEffect, useState } from "react";
import { AppHeader } from "@/components/layout/AppChrome";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";

/**
 * Legacy admin dashboard (routed at /app/admin/legacy). Shows real,
 * server-derived platform counts (users, clients, MRR, ARR, subscriptions).
 * No hardcoded numbers — every value comes from GET /api/dashboard/admin.
 *
 * ClientDashboardPage / TeamDashboardPage were removed — they were unused
 * dead code with chart JSX referencing seeded data.
 */
export function AdminDashboardPage() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard/admin").then((r) => setData(r.data)); }, []);
  if (!data) return (<div className="min-h-screen bg-background"><AppHeader /><div className="p-12 text-center text-muted-foreground">{t.common.loading}</div></div>);
  const m = data.metrics;
  const cards = [
    { label: t.dashboard.admin.users, value: m.users, testId: "admin-metric-users" },
    { label: t.dashboard.admin.clients, value: m.clients, testId: "admin-metric-clients" },
    { label: t.dashboard.admin.teamMembers, value: m.team_members, testId: "admin-metric-team" },
    { label: t.dashboard.admin.activeSubs, value: m.active_subscriptions, testId: "admin-metric-subs" },
    { label: t.dashboard.admin.mrr, value: `$${m.mrr}`, testId: "admin-metric-mrr" },
  ];
  const chartData = cards.slice(0, 4).map((c) => ({ name: c.label, value: typeof c.value === "number" ? c.value : 0 }));

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main data-testid="admin-dashboard" className="max-w-[1400px] mx-auto px-6 py-8">
        <h1 className="font-display text-3xl md:text-4xl">{t.dashboard.admin.title}</h1>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-8">
          {cards.map((c) => (
            <div key={c.label} data-testid={c.testId} className="nx-card p-6">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">{c.label}</div>
              <div className="font-display text-3xl mt-2">{c.value}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className="nx-card p-6 md:col-span-2" data-testid="admin-chart">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Platform overview</div>
            <div className="w-full h-64">
              <ResponsiveContainer>
                <BarChart data={chartData}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                  <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2 }} />
                  <Bar dataKey="value" fill="#002FA7" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="nx-card p-6" data-testid="admin-recent-users">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4">{t.dashboard.admin.recentUsers}</div>
            <ul className="space-y-3">
              {data.recent_users.map((u) => (
                <li key={u.id} className="flex items-center justify-between pb-3 border-b border-border/40 last:border-0">
                  <div>
                    <div className="text-sm font-medium">{u.name}</div>
                    <div className="text-xs text-muted-foreground">{u.email}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-1 border border-border rounded-sm">{u.role}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="nx-card p-6 mt-6" data-testid="admin-subs-table">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4">{t.dashboard.admin.subscriptions} · {data.subscriptions.length}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-widest text-muted-foreground">
                <tr className="text-start"><th className="text-start py-2 pe-4">Plan</th><th className="text-start py-2 pe-4">Cycle</th><th className="text-start py-2 pe-4">Amount</th><th className="text-start py-2 pe-4">Status</th><th className="text-start py-2 pe-4">Started</th></tr>
              </thead>
              <tbody>
                {data.subscriptions.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No subscriptions yet.</td></tr>}
                {data.subscriptions.map((s, i) => (
                  <tr key={i} className="border-t border-border/40">
                    <td className="py-3 pe-4">{s.plan_name}</td>
                    <td className="py-3 pe-4">{s.billing_cycle}</td>
                    <td className="py-3 pe-4">${s.amount}</td>
                    <td className="py-3 pe-4"><span className="text-emerald-500">{s.status}</span></td>
                    <td className="py-3 pe-4 text-muted-foreground">{new Date(s.started_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
